import { content } from '../scene/context.js';
import { rebuildHelpers } from '../scene/helpers.js';
import { applyLighting } from '../scene/lighting.js';
import { disposeNode, refitFrame, refitRecess, syncNode, syncParenting } from '../scene/objects.js';
import { requestRender } from '../state/bus.js';
import { pushHistory } from '../state/history.js';
import { S } from '../state/store.js';
import { nextIdFor } from '../core/doc.js';
import { registerDocFonts } from './fonts.js';

/* TEMPLATES — curated, read-only starting content shipped with the app
   (public/templates/), distinct from a user's own Presets.

   A template doc is CONTENT ONLY — fonts, objects, and the "look" (bg/tone/
   envInt/lighting) — never calibration or a photo. That's the one thing
   that must be true here even though presets themselves became full project
   snapshots this session: a template is meant to drop a professionally
   arranged starting point into a screen YOU already calibrated, not
   overwrite the screen/corners/photo that make this project yours. This is
   exactly the doc shape and apply logic presets used before that pivot —
   see services/presets.js's history for why it was originally built this
   way. Static files are self-contained (no assetIds to hydrate), so there's
   no signed-URL step here the way cloud presets need. */

/** @param {object} doc  as read straight from a public/templates/*.json file */
export async function applyTemplate(doc) {
  await registerDocFonts(doc);          // before syncNode: text traces immediately
  S.objects.forEach(o => { if(o.node.parent) o.node.parent.remove(o.node); disposeNode(o.node); });
  S.fonts = (doc.fonts || []).map(f => ({ ...f }));
  Object.assign(S, {
    bg: doc.bg, tone: doc.tone, envInt: doc.envInt, lighting: doc.lighting,
    aoStrength: doc.aoStrength ?? 0, aoReach: doc.aoReach ?? .15,
    dofStrength: doc.dofStrength ?? 0, dofFocus: { mode: 'screen', z: 0, id: null },
    objects: [], selId: null
  });
  doc.objects.forEach(o => {
    o.node = null;
    S.objects.push(o);
    /* A recess/frame from the template was authored against ITS screen size,
       not this project's. Auto-fill still means auto-fill — refit against
       the actual screen here before the first build, or a template made for
       a small screen ships an undersized opening onto a large one. */
    if (o.type === 'recess') refitRecess(o);
    if (o.type === 'frame') refitFrame(o);
    syncNode(o);
  });
  S.objects.forEach(o => {
    if(o.parentId != null && !S.objects.some(p=>p.id===o.parentId)) o.parentId = null;
  });
  S.objects.forEach(syncParenting);
  S.nextId = nextIdFor(S.objects);
  rebuildHelpers();
  applyLighting(true);
  requestRender();
  pushHistory();
}

let manifestCache = null;
export async function listTemplates() {
  if (manifestCache) return manifestCache;
  const res = await fetch('/templates/manifest.json');
  if (!res.ok) throw new Error('Could not load the template library.');
  manifestCache = await res.json();
  return manifestCache;
}

export async function loadTemplateDoc(slug) {
  const res = await fetch(`/templates/${slug}.json`);
  if (!res.ok) throw new Error('That template could not be loaded.');
  return res.json();
}
